import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, Clock, DollarSign, Package, FileText, 
  CheckCircle, XCircle, Eye, Sparkles, TrendingUp,
  AlertCircle, Calendar, User, Building2, ChevronRight,
  Zap, ShieldCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { useDarkMode } from '../../context/DarkModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarketplaceAFATab = ({ navigate }) => {
  const { isDarkMode } = useDarkMode();
  const [pendingCases, setPendingCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingCaseId, setProcessingCaseId] = useState(null);
  const user = auth.getUser();

  useEffect(() => {
    fetchPendingAFACases();
  }, []);

  const fetchPendingAFACases = async () => {
    try {
      setLoading(true);
      const token = auth.getToken();
      const response = await axios.get(`${API}/afa/marketplace/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCases(response.data.cases || []);
    } catch (error) {
      console.error('Error fetching AFA marketplace cases:', error);
      if (error.response?.status === 403) {
        toast.error('Du må ha en godkjent AFA-profil for å se markedsplassen');
      } else {
        toast.error('Kunne ikke hente AFA-saker');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptCase = async (caseId) => {
    try {
      setProcessingCaseId(caseId);
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/afa/marketplace/accept/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ AFA-sak akseptert!');
      
      // Remove accepted case from list
      setPendingCases(prev => prev.filter(c => c.id !== caseId));
      
      // Navigate to AFA workspace
      setTimeout(() => {
        navigate(`/lexy/afa/case/${caseId}`);
      }, 1000);
    } catch (error) {
      console.error('Error accepting case:', error);
      if (error.response?.status === 409) {
        toast.error('Saken ble akseptert av en annen advokat');
        fetchPendingAFACases(); // Refresh list
      } else if (error.response?.status === 403) {
        toast.error('Du må ha godkjent AFA-profil');
      } else {
        toast.error('Kunne ikke akseptere saken');
      }
    } finally {
      setProcessingCaseId(null);
    }
  };

  const handleSkipCase = async (caseId) => {
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/afa/marketplace/reject/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove skipped case from list (just hide it for now)
      setPendingCases(prev => prev.filter(c => c.id !== caseId));
      toast.info('Sak hoppet over');
    } catch (error) {
      console.error('Error skipping case:', error);
    }
  };

  const getPackageBadge = (packageLevel) => {
    const configs = {
      'BASIS': { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Basis' },
      'DOKUMENT': { color: 'bg-gray-100 text-indigo-700 border-indigo-200', label: 'Dokument' },
      'UTVIDET': { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Utvidet' }
    };
    const config = configs[packageLevel] || configs['BASIS'];
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        <Package className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatTimeAgo = (dateString: string | undefined) => {
    if (!dateString) return 'Akkurat nå';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Akkurat nå';
    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24) return `${diffHours} t siden`;
    return `${diffDays} dager siden`;
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-gray-950' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
            Laster AFA-markedsplassen...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 transition-colors duration-500 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-indigo-950' 
        : 'bg-gradient-to-br from-gray-50 via-white to-indigo-50/30'
    }`}>
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-violet-500 to-purple-600 bg-clip-text text-transparent mb-2">
              AFA Markedsplass
            </h1>
            <p className={`text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {pendingCases.length} {pendingCases.length === 1 ? 'sak' : 'saker'} venter på din respons
            </p>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`p-6 rounded-2xl border ${
              isDarkMode 
                ? 'bg-gray-900/50 border-gray-700/50' 
                : 'bg-white/80 border-gray-200'
            } backdrop-blur-sm shadow-lg`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Ventende saker
                </p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {pendingCases.length}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`p-6 rounded-2xl border ${
              isDarkMode 
                ? 'bg-gray-900/50 border-gray-700/50' 
                : 'bg-white/80 border-gray-200'
            } backdrop-blur-sm shadow-lg`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Potensielt honorar
                </p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {pendingCases.reduce((sum, c) => sum + (c.afa_package_price || 0), 0).toLocaleString('nb-NO')} kr
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`p-6 rounded-2xl border ${
              isDarkMode 
                ? 'bg-gray-900/50 border-gray-700/50' 
                : 'bg-white/80 border-gray-200'
            } backdrop-blur-sm shadow-lg`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Gjennomsnittspris
                </p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {pendingCases.length > 0 
                    ? Math.round(pendingCases.reduce((sum, c) => sum + (c.afa_package_price || 0), 0) / pendingCases.length).toLocaleString('nb-NO')
                    : '0'} kr
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Cases List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {pendingCases.length === 0 ? (
            <div className={`p-12 rounded-2xl border text-center ${
              isDarkMode 
                ? 'bg-gray-900/50 border-gray-700/50' 
                : 'bg-white/80 border-gray-200'
            } backdrop-blur-sm`}>
              <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-10 h-10 text-gray-500" />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Ingen ventende saker
              </h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Det er ingen AFA-saker tilgjengelig for øyeblikket. Sjekk tilbake senere.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence>
                {pendingCases.map((caseItem, index) => (
                  <motion.div
                    key={caseItem.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-6 rounded-2xl border ${
                      isDarkMode 
                        ? 'bg-gray-900/50 border-gray-700/50 hover:border-indigo-500/50' 
                        : 'bg-white/80 border-gray-200 hover:border-indigo-300'
                    } backdrop-blur-sm transition-all hover:shadow-xl`}
                  >
                    {/* Case Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-7 h-7 text-white" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            {getPackageBadge(caseItem.afa_package_level)}
                            {caseItem.afa_package_price && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                                <DollarSign className="w-3 h-3" />
                                {caseItem.afa_package_price.toLocaleString('nb-NO')} kr
                              </span>
                            )}
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatTimeAgo(caseItem.created_at)}
                            </span>
                          </div>
                          
                          <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {caseItem.topic || 'Juridisk sak'}
                          </h3>
                          
                          <p className={`text-sm line-clamp-2 mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {caseItem.intake_summary || 'Ingen beskrivelse tilgjengelig'}
                          </p>
                          
                          {/* Meta Info */}
                          <div className={`flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {caseItem.client_type && (
                              <span className="flex items-center gap-1">
                                {caseItem.client_type === 'COMPANY' ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}
                                {caseItem.client_type === 'COMPANY' ? 'Bedrift' : 'Privatperson'}
                              </span>
                            )}
                            {caseItem.case_type && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {caseItem.case_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => handleAcceptCase(caseItem.id)}
                        disabled={processingCaseId === caseItem.id}
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold py-3 rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                      >
                        {processingCaseId === caseItem.id ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Aksepterer...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Aksepter sak
                          </span>
                        )}
                      </Button>
                      
                      <Button
                        onClick={() => handleSkipCase(caseItem.id)}
                        disabled={processingCaseId === caseItem.id}
                        variant="outline"
                        className={`px-6 py-3 rounded-xl ${
                          isDarkMode 
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <XCircle className="w-5 h-5" />
                      </Button>
                      
                      <Button
                        onClick={() => navigate(`/lexy/afa/case/${caseItem.id}`)}
                        variant="outline"
                        className={`px-6 py-3 rounded-xl ${
                          isDarkMode 
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Eye className="w-5 h-5" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MarketplaceAFATab;
